import { ILoginService } from '../../../services/session/loginService';
import { tenantIdName } from '../../../config/constants';
import { AuthContextEnum } from '../../../services/authTokenService';
import authContextEnumToTenantLevel from '../../../services/authTokenService/utils/authContextEnumToTenantLevel';
import { ISessionService } from '../../../services/session';
import ITenantHandlerService from '../../../services/tenantHandler/ITenantHandlerService';
import { getCookie } from '../../../utils/cookies';
import { getStratusAccessToken } from '../../../utils/getStratusAccessToken';
import { decodeJWTPayload } from '../../../utils/tokenUtils/JWTUtils';
import { TokenType, UserContextEnum } from '../../types';
import IAuthInterface from './IAuthInterface';
import bindAllMethods from '../../../utils/bindAllMethods';
import { IAuthProviderService } from '../../../services/authProviderService';

export type AuthInterfaceParam = {
  sessionService: ISessionService;
  tenantHandlerService: ITenantHandlerService;
  loginService: ILoginService;
  authProviderService: IAuthProviderService;
};

/* In fact, this is kind a AuthProvider facade */
export default class AuthInterface implements IAuthInterface {
  private _sessionService: ISessionService;
  private _tenantHandlerService: ITenantHandlerService;
  private _loginService: ILoginService;
  private _authProviderService: IAuthProviderService;

  constructor(services: AuthInterfaceParam) {
    this._sessionService = services.sessionService;
    this._tenantHandlerService = services.tenantHandlerService;
    this._loginService = services.loginService;
    this._authProviderService = services.authProviderService;

    bindAllMethods(this);
  }

  /**
   * Custom toJSON method to control JSON serialization.
   * This prevents private properties from being included when using JSON.stringify
   */
  toJSON(): object {
    // Return only the public methods and public properties you want to include in JSON representation
    return this.getInterface();
  }

  public getInterface(): IAuthInterface {
    return {
      getAccessToken: this.getAccessToken,
      getIDToken: this.getIDToken,
      onTokenExchangeRequired: this.onTokenExchangeRequired,
      forceLogin: this.forceLogin,
      getDecodedUserStratusJWTPayload: this.getDecodedUserStratusJWTPayload,
      createOrglessAuthProvider: this.createOrglessAuthProvider,
      createOrgedAuthProvider: this.createOrgedAuthProvider,
      createAuthProviderByUserContextEnum:
        this.createAuthProviderByUserContextEnum
    };
  }

  /**
   * Gets an access token, automatically refreshing if it's expired or about to expire
   * @param forceRefresh Force a refresh of the token regardless of its expiration state
   * @returns A valid access token
   */
  public async getAccessToken(forceRefresh?: boolean): Promise<string> {
    return this._authProviderService.getAccessToken(forceRefresh);
  }

  public async getIDToken(): Promise<string> {
    return this._sessionService.getIdToken();
  }

  /**
   * @deprecated Use v2.onTokenExchangeRequired() instead. Soon, v1.authProvider namespace will be deprecated.
   */
  public async onTokenExchangeRequired(
    userContext: UserContextEnum = UserContextEnum.organization,
    tenantId?: string
  ): Promise<void> {
    console.warn(
      'This method is deprecated. Use v2.onTokenExchangeRequired() instead. Soon, v1.authProvider namespace will be deprecated.'
    );
    await this._sessionService.refresh();

    const _tenantId = tenantId || getCookie(tenantIdName);
    let authContext;
    switch (userContext) {
      case UserContextEnum.customer:
        authContext = AuthContextEnum.subtenant;
        break;
      default:
        authContext = AuthContextEnum.tenant;
        break;
    }

    const level = authContextEnumToTenantLevel(authContext);
    await this._tenantHandlerService.setTenant(_tenantId, level, {
      reload: false
    });
  }

  public async forceLogin(): Promise<void> {
    return new Promise(() => {
      this._loginService.redirectToLogin({ forceLogin: true });
    });
  }

  public getDecodedUserStratusJWTPayload(
    cookie: TokenType = TokenType.stratusAccessToken
  ): any {
    const userToken = getStratusAccessToken(cookie);
    if (!userToken) return undefined;

    return decodeJWTPayload(userToken);
  }

  /**
   * Factory method to create an orgless auth provider instance
   * @returns New IAuthInterface instance configured for orgless context
   */
  public createOrglessAuthProvider(): IAuthInterface {
    const orglessAuthProviderService =
      this._authProviderService.createOrglessAuthProvider();

    return new AuthInterface({
      sessionService: this._sessionService,
      tenantHandlerService: this._tenantHandlerService,
      loginService: this._loginService,
      authProviderService: orglessAuthProviderService
    });
  }

  /**
   * Factory method to create an orged auth provider instance
   * @param tenantId Optional tenant ID for the orged provider
   * @returns New IAuthInterface instance configured for tenant context
   */
  public createOrgedAuthProvider(tenantId: string = ''): IAuthInterface {
    const orgedAuthProviderService =
      this._authProviderService.createOrgedAuthProvider(tenantId);

    return new AuthInterface({
      sessionService: this._sessionService,
      tenantHandlerService: this._tenantHandlerService,
      loginService: this._loginService,
      authProviderService: orgedAuthProviderService
    });
  }

  /**
   * Factory method to create an auth provider based on user context
   * @param userContext The user context enum to determine auth configuration
   * @returns New IAuthInterface instance configured for the specified context
   */
  public createAuthProviderByUserContextEnum(
    userContext: UserContextEnum
  ): IAuthInterface {
    const contextualAuthProviderService =
      this._authProviderService.createAuthProviderByUserContextEnum(
        userContext
      );

    return new AuthInterface({
      sessionService: this._sessionService,
      tenantHandlerService: this._tenantHandlerService,
      loginService: this._loginService,
      authProviderService: contextualAuthProviderService
    });
  }
}
